// src/features/companies/companiesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import companyService from '../../services/companyService';

const initialState = {
  companies: [],
  currentCompany: null,
  isLoading: false,
  error: null,
  meta: {
    page: 1,
    pages: 1,
    total: 0,
  },
};

// Action asynchrone pour récupérer toutes les compagnies
export const fetchCompanies = createAsyncThunk(
  'companies/fetchCompanies',
  async (params, { rejectWithValue }) => {
    try {
      const data = await companyService.getCompanies(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des compagnies'
      );
    }
  }
);

// Action asynchrone pour récupérer une compagnie par ID
export const fetchCompanyById = createAsyncThunk(
  'companies/fetchCompanyById',
  async (id, { rejectWithValue }) => {
    try {
      const data = await companyService.getCompanyById(id);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération de la compagnie'
      );
    }
  }
);

// Action asynchrone pour créer une compagnie
export const createCompany = createAsyncThunk(
  'companies/createCompany',
  async (companyData, { rejectWithValue }) => {
    try {
      const data = await companyService.createCompany(companyData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la création de la compagnie'
      );
    }
  }
);

// Action asynchrone pour mettre à jour une compagnie
export const updateCompany = createAsyncThunk(
  'companies/updateCompany',
  async ({ id, ...companyData }, { rejectWithValue }) => {
    try {
      const data = await companyService.updateCompany(id, companyData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la mise à jour de la compagnie'
      );
    }
  }
);

// Action asynchrone pour supprimer une compagnie
export const deleteCompany = createAsyncThunk(
  'companies/deleteCompany',
  async (id, { rejectWithValue }) => {
    try {
      await companyService.deleteCompany(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la suppression de la compagnie'
      );
    }
  }
);

// Action asynchrone pour activer/désactiver une compagnie
export const toggleCompanyStatus = createAsyncThunk(
  'companies/toggleCompanyStatus',
  async (id, { rejectWithValue, getState }) => {
    try {
      const { companies } = getState().companies;
      const company = companies.find(c => c._id === id);
      
      if (!company) {
        throw new Error('Compagnie non trouvée');
      }
      
      const updatedCompany = await companyService.updateCompany(id, {
        isActive: !company.isActive
      });
      
      return updatedCompany;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la modification du statut'
      );
    }
  }
);

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    resetCompanyError: (state) => {
      state.error = null;
    },
    resetCurrentCompany: (state) => {
      state.currentCompany = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Gestion de fetchCompanies
      .addCase(fetchCompanies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies = action.payload.companies;
        state.meta = {
          page: action.payload.page,
          pages: action.payload.pages,
          total: action.payload.total,
        };
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Gestion de fetchCompanyById
      .addCase(fetchCompanyById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCompany = action.payload;
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Gestion de createCompany
      .addCase(createCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies.unshift(action.payload);
        state.meta.total += 1;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Gestion de updateCompany
      .addCase(updateCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.companies.findIndex(
          (company) => company._id === action.payload._id
        );
        if (index !== -1) {
          state.companies[index] = action.payload;
        }
        if (state.currentCompany && state.currentCompany._id === action.payload._id) {
          state.currentCompany = action.payload;
        }
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Gestion de deleteCompany
      .addCase(deleteCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies = state.companies.filter(
          (company) => company._id !== action.payload
        );
        state.meta.total -= 1;
        if (state.currentCompany && state.currentCompany._id === action.payload) {
          state.currentCompany = null;
        }
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Gestion de toggleCompanyStatus
      .addCase(toggleCompanyStatus.fulfilled, (state, action) => {
        const index = state.companies.findIndex(
          (company) => company._id === action.payload._id
        );
        if (index !== -1) {
          state.companies[index] = action.payload;
        }
        if (state.currentCompany && state.currentCompany._id === action.payload._id) {
          state.currentCompany = action.payload;
        }
      });
  },
});

export const { resetCompanyError, resetCurrentCompany } = companiesSlice.actions;
export default companiesSlice.reducer;